import { Component, useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { BuildingCanvas } from "@/components/three/BuildingScene";
import { BuildTimeline } from "@/components/BuildTimeline";
import { BUILD_STAGES } from "@/data/content";
import { useDeviceTier } from "@/hooks/useDeviceTier";
import { cn } from "@/lib/utils";

class CanvasGuard extends Component {
    constructor(props) { super(props); this.state = { failed: false }; }
    static getDerivedStateFromError() { return { failed: true }; }
    render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

const STATIC_FALLBACK = (
    <div className="mx-auto max-w-xl">
        {BUILD_STAGES.map((s, i) => (
            <p key={s.key} className="border-b border-white/10 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white/70">
                <span className="mr-3 text-flame">{String(i + 1).padStart(2, "0")}</span>{s.key}
            </p>
        ))}
    </div>
);

export const PlotToKey = () => {
    const tier = useDeviceTier();
    const targetRef = useRef(null);
    const progressRef = useRef(0);
    const [stage, setStage] = useState(0);
    const [final, setFinal] = useState(false);

    const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start start", "end end"] });
    useMotionValueEvent(scrollYProgress, "change", (v) => {
        progressRef.current = v;
        const s = Math.min(8, Math.floor(v * 9.4));
        setStage((prev) => (prev === s ? prev : s));
        setFinal(v > 0.96);
    });

    if (tier === "lite") {
        return (
            <section id="experience" data-testid="plot-to-key-section" className="blueprint relative overflow-hidden py-24">
                <div className="mx-auto max-w-[1440px] px-4 sm:px-6">
                    <p className="text-center font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-flame">From Plot to Final Key</p>
                    <h2 className="mt-4 text-center font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                        Watch a building come alive.
                    </h2>
                    <p className="mx-auto mt-4 max-w-md text-center text-sm text-white/60">
                        Every OMSIVO project moves through the same defined construction stages — scroll through them.
                    </p>
                    <div className="mt-14"><BuildTimeline /></div>
                </div>
            </section>
        );
    }

    return (
        <section id="experience" ref={targetRef} data-testid="plot-to-key-section" className="blueprint relative h-[440vh]">
            <div className="sticky top-0 flex h-screen items-stretch overflow-hidden">
                <CanvasGuard fallback={
                    <div className="flex h-full w-full items-center justify-center px-6">{STATIC_FALLBACK}</div>
                }>
                    <div className="absolute inset-0">
                        <BuildingCanvas progressRef={progressRef} />
                    </div>
                </CanvasGuard>

                <div className="pointer-events-none absolute inset-0 z-10 flex flex-col justify-between p-6 lg:p-12">
                    <div className="flex items-start justify-between gap-6">
                        <div>
                            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-flame">Scroll to build · From plot to final key</p>
                            <h2 className="mt-3 font-display text-3xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-4xl lg:text-5xl">
                                Watch a building<br />come alive.
                            </h2>
                        </div>
                        <div className="hidden flex-col items-end gap-1 md:flex" data-testid="plot-stage-rail">
                            {BUILD_STAGES.map((s, i) => (
                                <div key={s.key} className={cn("flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] transition-all duration-500",
                                    i === stage ? "text-flame" : i < stage ? "text-white/60" : "text-white/25")}>
                                    <span>{s.key}</span>
                                    <span className={cn("h-px transition-all duration-500", i === stage ? "w-10 bg-flame" : "w-5 bg-white/25")} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-end justify-between gap-6">
                        <AnimatePresence mode="wait">
                            <motion.div key={stage} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
                                transition={{ duration: 0.4 }} className="max-w-md">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame" data-testid="plot-stage-counter">
                                    Stage {String(stage + 1).padStart(2, "0")} / 09
                                </p>
                                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-wide text-white sm:text-3xl" data-testid="plot-stage-name">
                                    {BUILD_STAGES[stage].key}
                                </h3>
                                <p className="mt-2 text-sm text-white/65 sm:text-base">{BUILD_STAGES[stage].text}</p>
                            </motion.div>
                        </AnimatePresence>
                        <div className="hidden h-40 w-px bg-white/15 sm:block">
                            <motion.div className="w-px bg-flame" style={{ height: `${Math.min(100, (stage + 1) / 9 * 100)}%` }} />
                        </div>
                    </div>
                </div>

                <AnimatePresence>
                    {final && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            transition={{ duration: 0.8 }}
                            className="absolute inset-0 z-20 flex items-center justify-center bg-omsivo-deep/60 backdrop-blur-[3px]"
                            data-testid="plot-final-overlay">
                            <div className="px-6 text-center">
                                <motion.p initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
                                    className="font-display text-3xl font-black uppercase leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                                    From your first idea
                                </motion.p>
                                <motion.p initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.45, duration: 0.8 }}
                                    className="mt-2 font-display text-3xl font-black uppercase leading-tight tracking-tight text-flame sm:text-5xl lg:text-6xl">
                                    to your final key.
                                </motion.p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};
